import type {LatencyTier} from './types'
import {clientConfigSeverityTier} from './clientConfig'
import type {ClientConfigDiagnosticsResult, MediaDiagnosticsResult} from './types'

/**
 * Latency thresholds from Amazon Connect Endpoint Test Utility help text.
 * Classification uses average latency only.
 */
export const LATENCY_THRESHOLDS = {
  success: {low: 0, high: 200},
  caution: {low: 201, high: 275},
  warning: {low: 276, high: 500},
  failure: {low: 501, high: Number.MAX_VALUE},
} as const

export const classifyLatencyTier = (averageLatencyMs: number, allSamplesSucceeded: boolean): LatencyTier => {
  if (!allSamplesSucceeded) return 'failure'
  const avg = Math.round(averageLatencyMs)
  if (avg <= LATENCY_THRESHOLDS.success.high) return 'success'
  if (avg <= LATENCY_THRESHOLDS.caution.high) return 'caution'
  if (avg <= LATENCY_THRESHOLDS.warning.high) return 'warning'
  return 'failure'
}

const TIER_RANK: Record<LatencyTier, number> = {
  success: 0,
  caution: 1,
  warning: 2,
  failure: 3,
}

export const worstLatencyTier = (tiers: LatencyTier[]): LatencyTier => {
  if (tiers.length === 0) return 'failure'
  return tiers.reduce((worst, tier) => (TIER_RANK[tier] > TIER_RANK[worst] ? tier : worst), 'success')
}

export const mediaSeverityTier = (media: MediaDiagnosticsResult): LatencyTier => {
  if (media.regions.length === 0) return 'warning'
  if (media.success) return 'success'
  const anySuccess = media.regions.some((r) => r.endpoints.some((e) => e.success))
  return anySuccess ? 'warning' : 'failure'
}

/** Combine latency + media + client into a single overall tier. */
export const combineDiagnosticsTier = (args: {
  latencyTier: LatencyTier
  media: MediaDiagnosticsResult
  clientConfig: ClientConfigDiagnosticsResult
}): LatencyTier =>
  worstLatencyTier([
    args.latencyTier,
    mediaSeverityTier(args.media),
    clientConfigSeverityTier(args.clientConfig),
  ])
